import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import BadgeValidationModal from 'src/components/BadgeValidationModal/BadgeValidationModal'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { actions } from '../../RestockingState'
import { UnlinkOrigin } from '../../RestockingTypes'
import UnlinkPageButton from './UnlinkPageButton'

const DEFAULT_CONFIRM_MESSAGE = 'A página será pausada e liberada para outro repositor continuar a reposição.'
const DEFAULT_SUCCESS_MESSAGE = 'Keeper desvinculado e página pausada.'

interface UnlinkPageWithBadgeProps {
  idPage: number | undefined
  origin: UnlinkOrigin
  disabled?: boolean
  tooltip?: string
  disabledTooltip?: string
  confirmTitle?: string
  confirmMessage?: string
  confirmDataTestId?: string
  successMessage?: string
  onSuccess: () => void
  onCancel?: () => void
  renderTrigger?: (openFlow: () => void) => React.ReactNode
  /**
   * Modo controlado: use quando o gatilho vive fora deste componente — por exemplo dentro de um
   * modal que fecha ao abrir o fluxo, o que desmontaria um gatilho interno, ou numa linha de
   * tabela (gatilho `UnlinkPageButton` na linha, fluxo fora da tabela).
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface FormData {
  badgeSupervisor: string
}

const UnlinkPageWithBadge = ({
  idPage,
  origin,
  disabled,
  tooltip,
  disabledTooltip,
  confirmTitle = 'Desvincular keeper',
  confirmMessage = DEFAULT_CONFIRM_MESSAGE,
  confirmDataTestId = 'confirm-unlink-page-modal-test-id',
  successMessage = DEFAULT_SUCCESS_MESSAGE,
  onSuccess,
  onCancel,
  renderTrigger,
  open,
  onOpenChange,
}: UnlinkPageWithBadgeProps) => {
  const dispatch = useDispatch()
  const isControlled = open !== undefined
  const [internalFlowOpen, setInternalFlowOpen] = useState(false)
  const [showBadgeModal, setShowBadgeModal] = useState(false)

  const isFlowOpen = isControlled ? open : internalFlowOpen
  const showConfirmModal = isFlowOpen && !showBadgeModal

  const {
    register,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<FormData>()

  const setFlowOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value)
      return
    }
    setInternalFlowOpen(value)
  }

  const openFlow = () => setFlowOpen(true)

  const closeFlow = () => {
    clearErrors('badgeSupervisor')
    reset()
    setShowBadgeModal(false)
    setFlowOpen(false)
  }

  const cancelFlow = () => {
    closeFlow()
    onCancel?.()
  }

  const askForBadge = () => setShowBadgeModal(true)

  const backToConfirm = () => {
    clearErrors('badgeSupervisor')
    reset()
    setShowBadgeModal(false)
  }

  const handleSuccess = () => {
    closeFlow()
    toast.success(successMessage)
    onSuccess()
  }

  const unlinkOperator = () => {
    if (!idPage) return

    const badgeSupervisor = getValues('badgeSupervisor')

    if (!badgeSupervisor) {
      setError('badgeSupervisor', { message: 'Campo obrigatório' })
      return
    }

    dispatch(actions.operatorUnlink({
      badge: badgeSupervisor,
      idPage,
      origin,
      onSuccess: handleSuccess,
    }))
  }

  const defaultTrigger = (
    <UnlinkPageButton
      disabled={disabled}
      tooltip={tooltip}
      disabledTooltip={disabledTooltip}
      onClick={openFlow}
    />
  )

  return (
    <>
      {renderTrigger?.(openFlow) ?? (isControlled ? null : defaultTrigger)}
      <ConfirmOrRejectModal
        show={showConfirmModal}
        title={confirmTitle}
        description={confirmMessage}
        confirmButtonText='Desvincular'
        rejectButtonText='Voltar'
        onConfirm={askForBadge}
        onReject={cancelFlow}
        onClose={cancelFlow}
        dataTestId={confirmDataTestId}
      />
      <BadgeValidationModal
        show={showBadgeModal}
        title='Crachá do fiscal'
        dataTestId='supervisor-confirmation-modal-test-id'
        onClose={backToConfirm}
        actionFunction={unlinkOperator}
        register={register}
        inputName='badgeSupervisor'
        errors={errors.badgeSupervisor}
        errorMessage={errors.badgeSupervisor?.message}
      />
    </>
  )
}

export default UnlinkPageWithBadge
